import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Slider,
    Box,
    TextField,
    Grid,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../hooks';
import { selectInitialRootIOPS, selectRootBlockIOPS, selectGameStatus, selectTrafficPattern } from '../../store/game/selectors';
import { updateInitialRootIOPS, updateCurrentRootIOPS, updateTrafficPattern } from '../../store/game/gameSlice';
import { ContentTypes, IOPS, OperationTypes } from '../../models/block';
import { TrafficPatterns } from '../../models/traffic-patterns';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WavesIcon from '@mui/icons-material/Waves';
import StraightIcon from '@mui/icons-material/Straight';

interface IOPSConfigModalProps {
    open: boolean;
    onClose: () => void;
}

const IOPSConfigModal: React.FC<IOPSConfigModalProps> = ({ open, onClose }) => {
    const dispatch = useDispatch();
    const initialIOPS = useAppSelector((state) => selectInitialRootIOPS(state.game));
    const currentRootIOPS = useAppSelector((state) => selectRootBlockIOPS(state.game));
    const gameStatus = useAppSelector((state) => selectGameStatus(state.game));
    const currentTrafficPattern = useAppSelector((state) => selectTrafficPattern(state.game));


    const [totalIOPS, setTotalIOPS] = useState<number>(20);
    const [readWriteRatio, setReadWriteRatio] = useState<number>(50); // % Read
    const [readStaticDynamicRatio, setReadStaticDynamicRatio] = useState<number>(50); // % Static
    const [writeStaticDynamicRatio, setWriteStaticDynamicRatio] = useState<number>(0); // % Static (usually 0 for write, but configurable)

    // Default to STEADY if undefined
    const [trafficPattern, setTrafficPattern] = useState<TrafficPatterns>(TrafficPatterns.STEADY);

    useEffect(() => {
        const iopsToUse = gameStatus.isStarted ? currentRootIOPS : initialIOPS;

        if (iopsToUse) {
            setTotalIOPS(iopsToUse.total);

            const readTotal = iopsToUse.byOperationType[OperationTypes.READ]?.total || 0;
            const writeTotal = iopsToUse.byOperationType[OperationTypes.WRITE]?.total || 0;
            const total = readTotal + writeTotal;

            if (total > 0) {
                setReadWriteRatio(Math.round((readTotal / total) * 100));
            }

            const readStatic = iopsToUse.byOperationType[OperationTypes.READ]?.byContentType[ContentTypes.STATIC] || 0;
            if (readTotal > 0) {
                setReadStaticDynamicRatio(Math.round((readStatic / readTotal) * 100));
            }

            const writeStatic = iopsToUse.byOperationType[OperationTypes.WRITE]?.byContentType[ContentTypes.STATIC] || 0;
            if (writeTotal > 0) {
                setWriteStaticDynamicRatio(Math.round((writeStatic / writeTotal) * 100));
            }
        }

        if (currentTrafficPattern) {
            setTrafficPattern(currentTrafficPattern);
        }
    }, [initialIOPS, currentRootIOPS, gameStatus.isStarted, open, currentTrafficPattern]);

    const handleSave = () => {
        const readTotal = Math.round(totalIOPS * (readWriteRatio / 100));
        const writeTotal = totalIOPS - readTotal;

        const readStatic = Math.round(readTotal * (readStaticDynamicRatio / 100));
        const readDynamic = readTotal - readStatic;

        const writeStatic = Math.round(writeTotal * (writeStaticDynamicRatio / 100));
        const writeDynamic = writeTotal - writeStatic;

        const newIOPS: IOPS = {
            total: totalIOPS,
            byOperationType: {
                [OperationTypes.READ]: {
                    total: readTotal,
                    byContentType: {
                        [ContentTypes.STATIC]: readStatic,
                        [ContentTypes.DYNAMIC]: readDynamic,
                    },
                },
                [OperationTypes.WRITE]: {
                    total: writeTotal,
                    byContentType: {
                        [ContentTypes.STATIC]: writeStatic,
                        [ContentTypes.DYNAMIC]: writeDynamic,
                    },
                },
            },
        };

        if (gameStatus.isStarted) {
            dispatch(updateCurrentRootIOPS(newIOPS));
        } else {
            dispatch(updateInitialRootIOPS(newIOPS));
        }

        dispatch(updateTrafficPattern(trafficPattern));
        onClose();
    };

    const getPatternIcon = (pattern: TrafficPatterns) => {
        switch (pattern) {
            case TrafficPatterns.STEADY:
                return <StraightIcon fontSize="large" sx={{ transform: 'rotate(90deg)' }} />;
            case TrafficPatterns.STEADY_WAVE:
                return <WavesIcon fontSize="large" />;
            case TrafficPatterns.INCREASE_BY_LEVEL:
                return <TrendingUpIcon fontSize="large" />;
            case TrafficPatterns.INCREASE_BY_LEVEL_WITH_BACK_PRESSURE:
                return <ShowChartIcon fontSize="large" />;
            default:
                return <StraightIcon fontSize="large" sx={{ transform: 'rotate(90deg)' }} />;
        }
    };

    const getPatternLabel = (pattern: TrafficPatterns) => {
        switch (pattern) {
            case TrafficPatterns.STEADY:
                return "Steady";
            case TrafficPatterns.STEADY_WAVE:
                return "Steady Wave";
            case TrafficPatterns.INCREASE_BY_LEVEL:
                return "Increase by Level";
            case TrafficPatterns.INCREASE_BY_LEVEL_WITH_BACK_PRESSURE:
                return "Increase w/ Back Pressure";
            default:
                return "Unknown";
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Configure Traffic Simulation</DialogTitle>
            <DialogContent>
                <Grid container spacing={4}>
                    {/* Left Column: Traffic Pattern */}
                    <Grid item xs={12} md={6}>
                        <Typography variant="h6" gutterBottom>Traffic Pattern</Typography>
                        <Typography variant="body2" color="textSecondary" paragraph>
                            Choose how the traffic load behaves over time.
                        </Typography>
                        <Grid container spacing={2}>
                            {Object.values(TrafficPatterns).map((pattern) => (
                                <Grid item xs={6} key={pattern}>
                                    <Button
                                        variant={trafficPattern === pattern ? "contained" : "outlined"}
                                        color="primary"
                                        fullWidth
                                        onClick={() => setTrafficPattern(pattern)}
                                        sx={{
                                            height: '100px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 1,
                                            textTransform: 'none'
                                        }}
                                    >
                                        {getPatternIcon(pattern)}
                                        <Typography variant="caption" align='center'>
                                            {getPatternLabel(pattern)}
                                        </Typography>
                                    </Button>
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>

                    {/* Right Column: Load Configuration */}
                    <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" gutterBottom>Total Load</Typography>
                            <Typography variant="caption" color="textSecondary" paragraph>
                                Base TPS (Transactions Per Second)
                            </Typography>
                            <TextField
                                label="Total TPS"
                                type="number"
                                value={totalIOPS}
                                onChange={(e) => setTotalIOPS(Number(e.target.value))}
                                fullWidth
                                inputProps={{ min: 1 }}
                            />
                        </Box>

                        <Box sx={{ mb: 4, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                            <Typography variant="h6" gutterBottom>Traffic Composition</Typography>

                            <Box sx={{ mb: 3 }}>
                                <Typography gutterBottom variant="subtitle1">
                                    Read / Write Split
                                </Typography>
                                <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                                    {readWriteRatio}% Read / {100 - readWriteRatio}% Write
                                </Typography>
                                <Slider
                                    value={readWriteRatio}
                                    onChange={(_, value) => setReadWriteRatio(value as number)}
                                    valueLabelDisplay="auto"
                                    min={0}
                                    max={100}
                                />
                            </Box>

                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <Box sx={{ p: 2, bgcolor: '#f9f9f9', borderRadius: 1 }}>
                                        <Typography gutterBottom variant="subtitle2">Read Content</Typography>
                                        <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                                            {readStaticDynamicRatio}% Static / {100 - readStaticDynamicRatio}% Dynamic
                                        </Typography>
                                        <Slider
                                            value={readStaticDynamicRatio}
                                            onChange={(_, value) => setReadStaticDynamicRatio(value as number)}
                                            valueLabelDisplay="auto"
                                            min={0}
                                            max={100}
                                            disabled={readWriteRatio === 0}
                                            size="small"
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box sx={{ p: 2, bgcolor: '#f9f9f9', borderRadius: 1 }}>
                                        <Typography gutterBottom variant="subtitle2">Write Content</Typography>
                                        <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                                            {writeStaticDynamicRatio}% Static / {100 - writeStaticDynamicRatio}% Dynamic
                                        </Typography>
                                        <Slider
                                            value={writeStaticDynamicRatio}
                                            onChange={(_, value) => setWriteStaticDynamicRatio(value as number)}
                                            valueLabelDisplay="auto"
                                            min={0}
                                            max={100}
                                            disabled={readWriteRatio === 100}
                                            size="small"
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>

                        <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>Preview:</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body2"><strong>Read:</strong> {Math.round(totalIOPS * (readWriteRatio / 100))}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2"><strong>Write:</strong> {totalIOPS - Math.round(totalIOPS * (readWriteRatio / 100))}</Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave} variant="contained" color="primary">
                    Save Configuration
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default IOPSConfigModal;
